import {
    DateField,
    FilterDropdown,
    List,
    TextField,
    useTable,
    getDefaultSortOrder,
} from "@refinedev/antd";
import { BaseRecord, HttpError, useTranslate, CrudFilters } from "@refinedev/core";
import { Input, Space, Table, Card, Tag, Select, Tooltip, Modal, Button, Descriptions } from "antd";
import { useState } from "react";
import { IAutomationTask, AutomationTaskStep, AutomationTaskStatus } from "../../models/automation-task";
import { EyeOutlined } from "@ant-design/icons";

export const AutomationTaskList = ({ hideHeader }: { hideHeader?: boolean } = {}) => {
    const _t = useTranslate();
    const [metadataModalVisible, setMetadataModalVisible] = useState(false);
    const [selectedMetadata, setSelectedMetadata] = useState<any>(null);

    const { tableProps } = useTable<IAutomationTask, HttpError>({
        resource: "automation-tasks",
        syncWithLocation: true,
        sorters: {
            mode: "server",
            initial: [
                {
                    field: "id",
                    order: "desc",
                },
                {
                    field: "createdAt",
                    order: "desc",
                },
            ],
        },
        filters: {
            mode: "server",
        },
    });

    const getStatusColor = (status: AutomationTaskStatus | string) => {
        switch (status) {
            case AutomationTaskStatus.pending:
                return "orange";
            case AutomationTaskStatus.in_progress:
                return "blue";
            case AutomationTaskStatus.completed:
                return "green";
            case AutomationTaskStatus.failed:
                return "red";
            default:
                return "default";
        }
    };

    const getStepLabel = (step: AutomationTaskStep | string) => {
        switch (step) {
            case AutomationTaskStep.decrypt:
                return "Decrypt";
            case AutomationTaskStep.analyze:
                return "Analyze";
            case AutomationTaskStep.tuner_search:
                return "Tuner Search";
            default:
                return step;
        }
    };

    const formatError = (error: any): string => {
        if (!error) return "";
        if (typeof error === "string") return error;
        if (error.stack) return error.stack;
        if (error.message) return error.message;
        return JSON.stringify(error);
    };

    const formatErrorWithHtml = (error: any): string => {
        const errorText = formatError(error);
        return errorText.replace(/\n/g, "<br>");
    };

    const parseMetadata = (metadata: any): any => {
        if (!metadata) return null;
        if (typeof metadata === "string") {
            try {
                return JSON.parse(metadata);
            } catch (e) {
                return { raw: metadata };
            }
        }
        return metadata;
    };

    const formatFieldLabel = (key: string): string => {
        return key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const renderMetadataField = (key: string, value: any) => {
        if (key === "stack" && typeof value === "string") {
            const stackHtml = value.replace(/\n/g, "<br>");
            return (
                <div
                    dangerouslySetInnerHTML={{ __html: stackHtml }}
                    style={{
                        fontFamily: "monospace",
                        fontSize: "12px",
                        lineHeight: "1.6",
                        wordBreak: "break-word",
                    }}
                />
            );
        }
        
        if (typeof value === "object" && value !== null) {
            return (
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">
                    {JSON.stringify(value, null, 2)}
                </pre>
            );
        }
        
        return <span>{String(value)}</span>;
    };

    const handleViewMetadata = (metadata: any) => {
        setSelectedMetadata(metadata);
        setMetadataModalVisible(true);
    };

    const handleCloseMetadataModal = () => {
        setMetadataModalVisible(false);
        setSelectedMetadata(null);
    };

    return (
        <List
            title={hideHeader ? false : undefined}
            breadcrumb={hideHeader ? false : undefined}
        >
            <Card className="shadow-lg">
                <Table {...tableProps} rowKey="id">
                    <Table.Column
                        dataIndex="id"
                        title={_t("automation-tasks.columns.id", "ID")}
                        sorter={{ multiple: 1 }}
                        defaultSortOrder={getDefaultSortOrder("id", tableProps.sorters)}
                        width={80}
                    />
                    <Table.Column
                        dataIndex="orderId"
                        title={_t("automation-tasks.columns.orderId", "Order")}
                        render={(value: any) => <span className="text-gray-400">{value}</span>}
                        filterDropdown={(props) => (
                            <FilterDropdown {...props}>
                                <Input
                                    placeholder={_t("automation-tasks.columns.orderId", "Order")}
                                    allowClear
                                    style={{ width: 200 }}
                                />
                            </FilterDropdown>
                        )}
                        width={120}
                    />
                    <Table.Column
                        dataIndex="step"
                        title={_t("automation-tasks.columns.step", "Step")}
                        sorter={{ multiple: 2 }}
                        defaultSortOrder={getDefaultSortOrder("step", tableProps.sorters)}
                        render={(value: AutomationTaskStep) => (
                            <Tag color="cyan">{getStepLabel(value)}</Tag>
                        )}
                        filterDropdown={(props) => (
                            <FilterDropdown {...props}>
                                <Select
                                    placeholder="Select Step"
                                    allowClear
                                    style={{ width: 200 }}
                                    options={[
                                        { label: "Decrypt", value: AutomationTaskStep.decrypt },
                                        { label: "Analyze", value: AutomationTaskStep.analyze },
                                        { label: "Tuner Search", value: AutomationTaskStep.tuner_search },
                                    ]}
                                />
                            </FilterDropdown>
                        )}
                        width={150}
                    />
                    <Table.Column
                        dataIndex="status"
                        title={_t("automation-tasks.columns.status", "Status")}
                        sorter={{ multiple: 3 }}
                        defaultSortOrder={getDefaultSortOrder("status", tableProps.sorters)}
                        render={(value: AutomationTaskStatus) => (
                            <Tag color={getStatusColor(value)}>
                                {value.charAt(0).toUpperCase() + value.slice(1).replace("_", " ")}
                            </Tag>
                        )}
                        filterDropdown={(props) => (
                            <FilterDropdown {...props}>
                                <Select
                                    placeholder="Select Status"
                                    allowClear
                                    style={{ width: 200 }}
                                    options={[
                                        { label: "Pending", value: AutomationTaskStatus.pending },
                                        { label: "In Progress", value: AutomationTaskStatus.in_progress },
                                        { label: "Completed", value: AutomationTaskStatus.completed },
                                        { label: "Failed", value: AutomationTaskStatus.failed },
                                    ]}
                                />
                            </FilterDropdown>
                        )}
                        width={150}
                    />
                    <Table.Column
                        dataIndex="error"
                        title={_t("automation-tasks.columns.error", "Error")}
                        render={(value: any) => {
                            if (!value) return <span className="text-gray-400">-</span>;
                            const errorText = formatError(value);
                            const errorHtml = formatErrorWithHtml(value);
                            const truncated = errorText.length > 50 
                                ? errorText.substring(0, 50) + "..." 
                                : errorText;
                            return (
                                <Tooltip 
                                    title={
                                        <div 
                                            dangerouslySetInnerHTML={{ __html: errorHtml }}
                                            style={{ maxWidth: '400px', wordBreak: 'break-word' }}
                                        />
                                    }
                                >
                                    <span 
                                        className="text-red-600"
                                        dangerouslySetInnerHTML={{ 
                                            __html: truncated.length > 50 
                                                ? errorHtml.substring(0, 50) + "..." 
                                                : errorHtml 
                                        }}
                                    />
                                </Tooltip>
                            );
                        }}
                        width={200}
                    />
                    <Table.Column
                        dataIndex="metadata"
                        title={_t("automation-tasks.columns.metadata", "Metadata")}
                        render={(value: any, record: any) => {
                            if (!value) return <span className="text-gray-400">-</span>;
                            return (
                                <Button
                                    type="link"
                                    icon={<EyeOutlined />}
                                    onClick={() => handleViewMetadata(value)}
                                    size="small"
                                >
                                    {_t("automation-tasks.view", "View")}
                                </Button>
                            );
                        }}
                        width={150}
                    />
                    <Table.Column
                        dataIndex="createdAt"
                        title={_t("automation-tasks.columns.createdAt", "Created At")}
                        sorter={{ multiple: 4 }}
                        defaultSortOrder={getDefaultSortOrder("createdAt", tableProps.sorters)}
                        render={(value: any) => (
                            <DateField value={value} format="DD/MM/YYYY HH:mm" />
                        )}
                        width={180}
                    />
                    <Table.Column
                        dataIndex="updatedAt"
                        title={_t("automation-tasks.columns.updatedAt", "Updated At")}
                        sorter={{ multiple: 5 }}
                        defaultSortOrder={getDefaultSortOrder("updatedAt", tableProps.sorters)}
                        render={(value: any) => (
                            <DateField value={value} format="DD/MM/YYYY HH:mm" />
                        )}
                        width={180}
                    />
                </Table>
            </Card>
            <Modal
                title={_t("automation-tasks.metadataModalTitle", "Task Metadata")}
                open={metadataModalVisible}
                onCancel={handleCloseMetadataModal}
                footer={[
                    <Button key="close" onClick={handleCloseMetadataModal}>
                        {_t("buttons.close", "Close")}
                    </Button>
                ]}
                width={900}
            >
                {selectedMetadata && (() => {
                    const parsedMetadata = parseMetadata(selectedMetadata);
                    if (!parsedMetadata || Object.keys(parsedMetadata).length === 0) {
                        return <div className="text-gray-400">{_t("automation-tasks.noMetadata", "No metadata")}</div>;
                    }
                    
                    return (
                        <Descriptions bordered column={1} size="small">
                            {Object.entries(parsedMetadata).map(([key, value]) => (
                                <Descriptions.Item
                                    key={key}
                                    label={formatFieldLabel(key)}
                                    labelStyle={{ fontWeight: "600", width: "200px" }}
                                >
                                    {renderMetadataField(key, value)}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    );
                })()}
            </Modal>
        </List>
    );
};

